import { type FormEvent, type ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * Body width. The three tiers replace the per-dialog pixel widths that had drifted apart
 * (420/460/480/520/560): `sm` for a dialog of one or two fields, `md` for the ordinary form,
 * `lg` for one whose body is a list the author reads before choosing.
 */
const SIZE_CLASS = {
  sm: "sm:max-w-[460px]",
  md: "sm:max-w-[520px]",
  lg: "sm:max-w-[560px]",
} as const;

export interface AdminFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The thing being created or edited. Also the dialog's accessible name. */
  title: ReactNode;
  /** What submitting will do — scope, consequences, what happens next. */
  description?: ReactNode;
  /** The verb on the submit button ("Create framework", "Add rule"), not "Save" or "OK". */
  submitLabel: ReactNode;
  /** Shown on the submit button while the submit is in flight. Defaults to `submitLabel`. */
  pendingLabel?: ReactNode;
  cancelLabel?: ReactNode;
  /**
   * Runs on submit — from the button, or from Enter in any field, because the body is inside a
   * real `form`. The dialog does **not** close itself: whether a failed submit leaves it open is
   * the caller's decision, as it is for `ConfirmDialog`. A returned promise drives the pending
   * state; the caller still owns reporting its failure.
   */
  onSubmit: () => void | Promise<void>;
  /** Caller-owned pending state, for submits whose in-flight flag lives outside this dialog. */
  isPending?: boolean;
  /** The caller's validity gate. Pending already disables submit on its own. */
  submitDisabled?: boolean;
  /**
   * A blocking notice shown just above the footer — why submit is refused right now. It is
   * drawn in the amber family's content token, which carries the caution in both themes; a
   * per-field message belongs beside its field instead.
   */
  error?: ReactNode;
  size?: keyof typeof SIZE_CLASS;
  /** The fields. Laid out as a column; group tighter runs in a `div` of your own. */
  children: ReactNode;
}

/**
 * The one admin form dialog: a title, what it does, fields, Cancel, and one verb.
 *
 * Every square-cornered admin create/edit dialog renders this rather than its own
 * `Dialog > DialogContent > form > DialogHeader > … > DialogFooter` scaffold, so the width tiers,
 * the title scale, the body padding, the footer button shape and the disabled-while-pending
 * behaviour are decided once instead of drifting per call site.
 *
 * The body scrolls and the footer does not, so Cancel and submit stay reachable on a short
 * viewport no matter how long the form grows.
 *
 * Anything that is not a form — a confirmation (use `ConfirmDialog`), a multi-step flow, a
 * preview — keeps its own `Dialog`.
 */
export function AdminFormDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  pendingLabel,
  cancelLabel = "Cancel",
  onSubmit,
  isPending,
  submitDisabled,
  error,
  size = "md",
  children,
}: AdminFormDialogProps) {
  const [awaitingSubmit, setAwaitingSubmit] = useState(false);
  const pending = isPending === true || awaitingSubmit;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending || submitDisabled === true) return;
    const result = onSubmit();
    if (!(result instanceof Promise)) return;
    setAwaitingSubmit(true);
    const settle = () => setAwaitingSubmit(false);
    result.then(settle, settle);
  };

  return (
    <Dialog open={open} onOpenChange={pending ? undefined : onOpenChange}>
      <DialogContent
        className={cn(
          "flex max-h-[85vh] flex-col gap-0 overflow-hidden rounded-none p-0",
          SIZE_CLASS[size]
        )}
        showCloseButton={false}
      >
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto p-8 pb-6">
            <DialogHeader className="gap-2">
              <DialogTitle className="text-[24px] font-semibold leading-tight tracking-[-0.4px]">
                {title}
              </DialogTitle>
              {description === undefined || description === null ? null : (
                <DialogDescription className="text-[14px] leading-[1.5] text-muted-foreground">
                  {description}
                </DialogDescription>
              )}
            </DialogHeader>
            {children}
          </div>

          <div className="flex flex-col gap-3 px-8 pb-8">
            {error === undefined || error === null ? null : (
              <p className="text-[12px] leading-5 text-amber-content">{error}</p>
            )}
            <DialogFooter className="gap-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                disabled={pending}
                onClick={() => onOpenChange(false)}
                className="h-10 rounded-none px-4 text-[14px]"
              >
                {cancelLabel}
              </Button>
              <Button
                type="submit"
                disabled={pending || submitDisabled === true}
                className="h-10 rounded-none px-[18px] text-[14px]"
              >
                {pending && pendingLabel !== undefined ? pendingLabel : submitLabel}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
